import { ResizeMode } from '@wix/thumbnail-generator';
import { always, cond, evolve, max, multiply, pipe, T } from 'ramda';
import { round } from 'ramda-adjunct';
import React, { FC, useMemo } from 'react';
import { ThumbnailBackground } from '../../../../../../constants/ThumbnailBackground';
import { ActionClickOrigin } from '../../../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { getRectFitScale } from '../../../../../../utils/math';
import { MediaManagerSDKFile } from '../../../../../../utils/media-manager-sdk';
import {
  isPictureFile,
  isPNGPictureFile,
  isSecureFile,
  isShutterstockItem,
  isTransparentVideoFile,
  isVectorArtFile,
  isVideoFile,
} from '../../../../../../utils/media-manager-sdk/items';
import { isReady } from '../../../../../../utils/media-manager-sdk/transcoding';
import { FileThumbnail, ItemActionsRow } from '../../../../../../components/common';
import {
  ActionButton,
  ActionButtonSkin,
} from '../../../../../../components/common/ItemActionsRow/ItemActionsRow.types';
import { THUMBNAIL_SIZE } from '../../../../../../components/common/Thumbnails/constants';
import { GalleryFileControls } from '../../../../../../components/layouts/Gallery/GalleryFile/GalleryFileControls';
import styles from '../Thumbnails.scss';
import { IOuter } from './File.types';

const actionButtons = [ActionButton.CropAndEdit, ActionButton.Preview];

const MIN_WIDTH = 90;
const MIN_HEIGHT = 54;

const isFullSizeWrapper = cond<MediaManagerSDKFile, boolean>([
  [isVectorArtFile, always(true)],
  [isTransparentVideoFile, always(true)],
  [isPictureFile, always(false)],
  [isVideoFile, always(false)],
  [T, always(true)],
]);

export const File: FC<IOuter> = ({ item, onFilesExpand, currentFolder, keepOriginalSize, isPanel }) => {
  const isItemReady = isReady(item);

  let width = item.width || (item.smartGridSize && item.smartGridSize.width);
  let height = isTransparentVideoFile(item)
    ? Math.floor(item.height / 2)
    : item.height || (item.smartGridSize && item.smartGridSize.height);

  const itemHasNoWidth = !item.width && item.smartGridSize;

  if (isShutterstockItem(item)) {
    const fileInfo = item.fileInfo || { aspect: 1 };

    width = THUMBNAIL_SIZE.width;
    height = THUMBNAIL_SIZE.width / fileInfo.aspect;
  }

  const scale = getRectFitScale(THUMBNAIL_SIZE, { width, height });

  const wrapperIsFullSize = isFullSizeWrapper(item);

  const wrapperSize = wrapperIsFullSize
    ? THUMBNAIL_SIZE
    : evolve(
        {
          width: pipe(multiply(scale), round, max(MIN_WIDTH)),
          height: pipe(multiply(scale), round, max(MIN_HEIGHT)),
        },
        { width, height },
      );

  const backgroundSize = itemHasNoWidth && { backgroundSize: 'cover' };

  const isThumbnailWithBackground = wrapperIsFullSize || isPNGPictureFile(item);
  const thumbnailBackground = isThumbnailWithBackground
    ? ThumbnailBackground.LightBlueInfoPanel
    : ThumbnailBackground.Transparent;

  const wrapperStyle = useMemo(
    () => ({ backgroundColor: thumbnailBackground, ...backgroundSize }),
    [thumbnailBackground, backgroundSize],
  );

  const thumbnailParams = useMemo(
    () => ({ mode: wrapperIsFullSize ? ResizeMode.Fit : ResizeMode.Fill, keepOriginalSize }),
    [wrapperIsFullSize, keepOriginalSize],
  );

  return (
    <div data-hook="single-file-top-part">
      <FileThumbnail
        key={item.id}
        className={styles.thumbnail}
        wrapperClassName={styles.thumbnailWrapper}
        wrapperStyle={wrapperStyle}
        style={backgroundSize}
        file={item}
        {...wrapperSize}
        thumbnailBackground={thumbnailBackground}
        params={thumbnailParams}
        isPanel={isPanel}
      />

      <div className={styles.thumbnailControls}>
        {isItemReady && <GalleryFileControls file={item} onPlayClick={onFilesExpand} />}
      </div>

      <div className={styles.actionsWrapper}>
        <div className={styles.actionsWrapperInner} style={wrapperSize}>
          <ItemActionsRow
            skin={ActionButtonSkin.Light}
            item={item}
            currentFolder={currentFolder}
            buttons={actionButtons}
            isSecure={isSecureFile(item)}
            onOpenExpandedViewClick={onFilesExpand}
            className={styles.actionsWithPSEntryPoint}
            innerClassName={styles.actionButtonWithPSEntryPoint}
            isPanel
            actionsClickOrigin={ActionClickOrigin.InfoPanelPreview}
          />
        </div>
      </div>
    </div>
  );
};
